import { Box, Card, RadioButton, Text } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { PerformanceScoreConfigurationModeEnum } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import performanceWeightsUpsell from 'modules/performance_process/lib/assets/performanceWeightsUpsell.png'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

export type ScoreMode = PerformanceScoreConfigurationModeEnum | null | undefined
export type PublicProps = {
  mode: ScoreMode
  disabled?: boolean
  hasCustomScoreConfigurationEnabled: boolean
}

type Props = {
  onChange: (mode: ScoreMode) => void
} & PublicProps

export const ScoreModeSelector: React.FC<Props> = ({
  mode,
  disabled = false,
  onChange,
  hasCustomScoreConfigurationEnabled,
}) => {
  const isScoringEnabled = !!mode

  return (
    <Box gap="s16">
      <Text size="300">{i18n.t('performance_process.reviews.scoring_system.description')}</Text>

      <Box gap="s16">
        <Box key={'with_scoring_system'} gap="s16">
          <Card key={'with_scoring_system_custom_weights'}>
            <Card.Title
              action={
                <RadioButton
                  size="medium"
                  onChange={() => {
                    onChange('custom_weights')
                  }}
                  value="custom_section_weights"
                  checked={mode == 'custom_weights'}
                  disabled={!hasCustomScoreConfigurationEnabled || disabled}
                  aria-label={i18n.t(
                    'performance_process.reviews.scoring_system.with_scoring.custom_weights.title'
                  )}
                />
              }
            >
              {i18n.t(
                'performance_process.reviews.scoring_system.with_scoring.custom_weights.title'
              )}
            </Card.Title>

            <Card.Description>
              {i18n.t(
                'performance_process.reviews.scoring_system.with_scoring.custom_weights.description'
              )}
            </Card.Description>

            {!hasCustomScoreConfigurationEnabled && (
              <Card.Content>
                <UpsellingKit.ProductWidgetPopover
                  label={i18n.t('pricing_plans.generic_upgrade')}
                  mediaUrl={performanceWeightsUpsell}
                  variant="outlinePromote"
                  title={i18n.t('performance_process.upsells.weights.title_banner')}
                  description={i18n.t('performance_process.upsells.weights.description')}
                  requestSource="Performance - weights enterprise plan - wizard selector"
                  moduleRequested={AvailableModules.review_process}
                  featureRequested={Features.review_process_custom_score_configuration}
                  requestReason={ValidReasons.MissingFeature}
                />
              </Card.Content>
            )}
          </Card>

          <Card key={'with_scoring_system_final_question'}>
            <Card.Title
              action={
                <RadioButton
                  size="medium"
                  onChange={() => {
                    onChange('final_question')
                  }}
                  value="with_scoring_system_final_question"
                  checked={mode == 'final_question'}
                  disabled={disabled}
                  aria-label={i18n.t(
                    'performance_process.reviews.scoring_system.with_scoring.final_question.title'
                  )}
                />
              }
            >
              {i18n.t(
                'performance_process.reviews.scoring_system.with_scoring.final_question.title'
              )}
            </Card.Title>
            <Card.Description>
              {i18n.t(
                'performance_process.reviews.scoring_system.with_scoring.final_question.description'
              )}
            </Card.Description>
          </Card>
        </Box>

        <Box key={'no_scoring_system'}>
          <Card>
            <Card.Title
              action={
                <RadioButton
                  size="medium"
                  onChange={() => {
                    onChange(null)
                  }}
                  value="no_scoring_system"
                  checked={!isScoringEnabled}
                  disabled={disabled}
                  aria-label={i18n.t(
                    'performance_process.reviews.scoring_system.without_scoring.title'
                  )}
                />
              }
            >
              {i18n.t('performance_process.reviews.scoring_system.without_scoring.title')}
            </Card.Title>
            <Card.Description>
              {i18n.t('performance_process.reviews.scoring_system.without_scoring.description')}
            </Card.Description>
          </Card>
        </Box>
      </Box>
    </Box>
  )
}
